import { Chip } from '@liveauctioneers/hammer-ui-core/chips';
import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { SearchInput } from '@liveauctioneers/hammer-ui-core/searchInput';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { ReactElement, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useIsMobileLayout } from '@/hooks/useIsMobileLayout';
import {
    defaultAuctionsNearMeUkBounds,
    defaultAuctionsNearMeUsaBounds,
} from '@/pages/AuctionsNearMe/constants/AuctionsNearMeConstants';
import { useAppSelector } from '@/redux/hooks';
import { allGeoAreasSelector } from '@/redux/modules/geoArea/geoArea.selectors';
import { GeoArea } from '@/types/GeoArea';
import { getAuctionsNearMePageUrl, getAuctionsNearMeSearchUrl } from '@/utils/urls';

import { MapBounds } from '../GoogleMap/AuctionsNearMeGoogleMap';
import { getGeocoder } from './helpers/getGeocoder';

export type AuctionsNearMeSearchInputProps = {
    currentGeoArea: GeoArea | undefined | null;
    onSearchResults: (bounds: MapBounds) => void;
    parentSearchTerm: string;
    setGeocoderFailed: (failed: boolean) => void;
    setPageTitleSearchTerm: (term: string) => void;
};

export const AuctionsNearMeSearchInput = ({
    currentGeoArea,
    onSearchResults,
    parentSearchTerm,
    setGeocoderFailed,
    setPageTitleSearchTerm,
}: AuctionsNearMeSearchInputProps): ReactElement => {
    const isMobile = useIsMobileLayout();
    const google = typeof window !== 'undefined' ? window.google : null;
    const navigate = useNavigate();
    const { formatMessage, locale } = useIntl();
    const placeholder = formatMessage({
        id: 'auctionsNearMe.searchLocationPlaceholder',
    });
    const allGeoAreas = useAppSelector(allGeoAreasSelector);
    const geoAreaOptions = allGeoAreas.map((area) => area.geoAreaName);
    const [searchTerm, setSearchTerm] = useState('');

    // Sync local state when parent's searchTerm is cleared (e.g., navigating back to base page)
    useEffect(() => {
        if (parentSearchTerm === '') {
            setSearchTerm('');
        }
    }, [parentSearchTerm]);

    // Does not call onSearchResults — this navigates to the searched place's own URL
    // (?search=<term>), which resolves its own bounds on render. Setting bounds here too would
    // only matter for the instant before that navigation lands, and on a page with a GeoArea,
    // mapBounds favors the GeoArea over these bounds during that instant, leaving the map showing
    // the previous location while the results (fetched independently) move.
    const provideBoundsViaGeocoder = (selectedOption: string): void => {
        const geocoder = getGeocoder(google);
        geocoder.geocode({ address: selectedOption }, (results, status) => {
            if (status !== google.maps.GeocoderStatus.OK || results.length === 0) {
                setGeocoderFailed(true);
                return;
            }
            const { viewport } = results[0].geometry;

            if (!viewport) {
                setGeocoderFailed(true);
                return;
            }

            setGeocoderFailed(false);

            const searchUrl = getAuctionsNearMeSearchUrl(selectedOption);
            navigate(searchUrl);
        });
    };

    // Does not call onSearchResults — this navigates to the matched GeoArea's own URL, which
    // resolves that same GeoArea's bounds on render. See provideBoundsViaGeocoder above.
    const provideBoundsViaGeoArea = (selectedOption: string): void => {
        const geoArea = allGeoAreas.find((geoArea) => geoArea.geoAreaName.toString() === selectedOption);

        if (!geoArea) {
            return;
        }

        setGeocoderFailed(false);

        const url = getAuctionsNearMePageUrl(geoArea);
        navigate(url);
    };

    const provideBoundsViaDefault = (): void => {
        const defaultBounds = locale === 'en-gb' ? defaultAuctionsNearMeUkBounds : defaultAuctionsNearMeUsaBounds;
        setGeocoderFailed(false);
        onSearchResults(defaultBounds);
    };

    const onSubmit = (selectedOption: string): void => {
        if (!google) {
            return;
        }
        setSearchTerm(selectedOption);
        setPageTitleSearchTerm(selectedOption);
        if (geoAreaOptions.includes(selectedOption)) {
            provideBoundsViaGeoArea(selectedOption);
        } else if (selectedOption) {
            provideBoundsViaGeocoder(selectedOption);
        } else {
            provideBoundsViaDefault();
        }
    };

    const navigateToMainPage = () => {
        setSearchTerm('');
        setPageTitleSearchTerm('');
        setGeocoderFailed(false);
        provideBoundsViaDefault();
        navigate(getAuctionsNearMePageUrl());
    };

    const currentSearchTerm = searchTerm || currentGeoArea?.geoAreaName;

    return (
        <section className="flex flex-col smMin:flex-row">
            <div className="mr-xs w-full smMin:w-[184px]">
                <SearchInput
                    aria-label={formatMessage({
                        id: 'auctionsNearMe.ariaLabel.searchInput',
                    })}
                    onSubmit={onSubmit}
                    placeholder={placeholder}
                    small
                />
            </div>
            {Boolean(currentSearchTerm) && (
                <span className={cn({ 'my-xs mr-0': isMobile })}>
                    <Chip
                        aria-label={`Remove ${currentSearchTerm} filter`}
                        onClose={navigateToMainPage}
                        selected
                        style="input"
                    >
                        {currentSearchTerm}
                    </Chip>
                </span>
            )}
        </section>
    );
};

export default AuctionsNearMeSearchInput;
